import {
    CheckoutButtonInitializeOptions,
    CheckoutButtonStrategy,
    InvalidArgumentError,
    MissingDataError,
    MissingDataErrorType,
    PaymentIntegrationService,
} from '@bigcommerce/checkout-sdk/payment-integration-api';

import BigCommercePaymentsIntegrationService from '../bigcommerce-payments-integration-service';
import {
    ApproveCallbackActions,
    ApproveCallbackPayload,
    BigCommercePaymentsButtonsOptions,
    BigCommercePaymentsInitializationData,
    PayPalBuyNowInitializeOptions,
    ShippingAddressChangeCallbackPayload,
    ShippingOptionChangeCallbackPayload,
} from '../bigcommerce-payments-types';

import BigCommercePaymentsButtonInitializeOptions, {
    WithBigCommercePaymentsButtonInitializeOptions,
} from './bigcommerce-payments-button-initialize-options';

export default class BigCommercePaymentsButtonStrategy implements CheckoutButtonStrategy {
    constructor(
        private paymentIntegrationService: PaymentIntegrationService,
        private bigCommercePaymentsIntegrationService: BigCommercePaymentsIntegrationService,
    ) {}

    async initialize(
        options: CheckoutButtonInitializeOptions & WithBigCommercePaymentsButtonInitializeOptions,
    ): Promise<void> {
        const { bigcommerce_payments, containerId, methodId } = options;

        const isBuyNowFlow = Boolean(bigcommerce_payments?.buyNowInitializeOptions);

        if (!methodId) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.methodId" argument is not provided.',
            );
        }

        if (!containerId) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.containerId" argument is not provided.`,
            );
        }

        if (!bigcommerce_payments) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.bigcommerce_payments" argument is not provided.`,
            );
        }

        if (isBuyNowFlow && !bigcommerce_payments.currencyCode) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.bigcommerce_payments.currencyCode" argument is not provided.`,
            );
        }

        if (
            isBuyNowFlow &&
            typeof bigcommerce_payments.buyNowInitializeOptions?.getBuyNowCartRequestBody !==
                'function'
        ) {
            throw new InvalidArgumentError(
                `Unable to initialize payment because "options.bigcommerce_payments.buyNowInitializeOptions.getBuyNowCartRequestBody" argument is not provided or it is not a function.`,
            );
        }

        if (!isBuyNowFlow) {
            // Info: default checkout should not be loaded for BuyNow flow,
            // since there is no checkout session available for that.
            await this.paymentIntegrationService.loadDefaultCheckout();
        }

        // Info: we are using provided currency code for buy now cart,
        // because checkout session is not available before buy now cart creation,
        // hence application will throw an error on getCartOrThrow method call
        const currencyCode = isBuyNowFlow
            ? bigcommerce_payments.currencyCode
            : this.paymentIntegrationService.getState().getCartOrThrow().currency.code;

        await this.bigCommercePaymentsIntegrationService.loadPayPalSdk(
            methodId,
            currencyCode,
            false,
        );

        this.renderButton(containerId, methodId, bigcommerce_payments);
    }

    deinitialize(): Promise<void> {
        return Promise.resolve();
    }

    private renderButton(
        containerId: string,
        methodId: string,
        bigcommerce_payments: BigCommercePaymentsButtonInitializeOptions,
    ): void {
        const { buyNowInitializeOptions, style, onComplete, onEligibilityFailure } =
            bigcommerce_payments;

        const paypalSdk = this.bigCommercePaymentsIntegrationService.getPayPalSdkOrThrow();
        const state = this.paymentIntegrationService.getState();
        const paymentMethod =
            state.getPaymentMethodOrThrow<BigCommercePaymentsInitializationData>(methodId);
        const { isHostedCheckoutEnabled, isServerSideShippingCallbacksEnabled } =
            paymentMethod.initializationData || {};

        const defaultCallbacks = {
            createOrder: () =>
                this.bigCommercePaymentsIntegrationService.createOrder('bigcommerce_payments'),
            onApprove: ({ orderID }: ApproveCallbackPayload) =>
                this.bigCommercePaymentsIntegrationService.tokenizePayment(methodId, orderID),
        };

        const buyNowFlowCallbacks = {
            onClick: () => this.handleClick(buyNowInitializeOptions),
            onCancel: () => this.paymentIntegrationService.loadDefaultCheckout(),
        };

        const hostedCheckoutCallbacks = {
            ...(!isServerSideShippingCallbacksEnabled && {
                onShippingAddressChange: (data: ShippingAddressChangeCallbackPayload) =>
                    this.onShippingAddressChange(data),
                onShippingOptionsChange: (data: ShippingOptionChangeCallbackPayload) =>
                    this.onShippingOptionsChange(data),
            }),
            onApprove: (data: ApproveCallbackPayload, actions: ApproveCallbackActions) =>
                this.onHostedCheckoutApprove(
                    data,
                    actions,
                    methodId,
                    onComplete,
                    isServerSideShippingCallbacksEnabled,
                ),
        };

        const buttonRenderOptions: BigCommercePaymentsButtonsOptions = {
            fundingSource: paypalSdk.FUNDING.PAYPAL,
            style: this.bigCommercePaymentsIntegrationService.getValidButtonStyle(style),
            ...defaultCallbacks,
            ...(buyNowInitializeOptions && buyNowFlowCallbacks),
            ...(isHostedCheckoutEnabled && hostedCheckoutCallbacks),
        };

        const paypalButton = paypalSdk.Buttons(buttonRenderOptions);

        if (paypalButton.isEligible()) {
            paypalButton.render(`#${containerId}`);
        } else if (onEligibilityFailure && typeof onEligibilityFailure === 'function') {
            onEligibilityFailure();
        } else {
            this.bigCommercePaymentsIntegrationService.removeElement(containerId);
        }
    }

    private async handleClick(
        buyNowInitializeOptions?: PayPalBuyNowInitializeOptions,
    ): Promise<void> {
        if (buyNowInitializeOptions) {
            const buyNowCart =
                await this.bigCommercePaymentsIntegrationService.createBuyNowCartOrThrow(
                    buyNowInitializeOptions,
                );

            await this.paymentIntegrationService.loadCheckout(buyNowCart.id);
        }
    }

    private async onHostedCheckoutApprove(
        data: ApproveCallbackPayload,
        actions: ApproveCallbackActions,
        methodId: string,
        onComplete?: () => void,
        isServerSideShippingCallbacksEnabled?: boolean,
    ): Promise<boolean> {
        if (!data.orderID) {
            throw new MissingDataError(MissingDataErrorType.MissingOrderId);
        }

        const state = this.paymentIntegrationService.getState();
        const cart = state.getCartOrThrow();

        try {
            const hasPhysicalItems = cart.lineItems.physicalItems.length > 0;

            if (!isServerSideShippingCallbacksEnabled) {
                const orderDetails = await actions.order.get();

                const billingAddress =
                    this.bigCommercePaymentsIntegrationService.getBillingAddressFromOrderDetails(
                        orderDetails,
                    );

                await this.paymentIntegrationService.updateBillingAddress(billingAddress);

                if (hasPhysicalItems) {
                    const shippingAddress =
                        this.bigCommercePaymentsIntegrationService.getShippingAddressFromOrderDetails(
                            orderDetails,
                        );

                    await this.paymentIntegrationService.updateShippingAddress(shippingAddress);
                }
            }

            if (hasPhysicalItems) {
                await this.bigCommercePaymentsIntegrationService.updateOrder(
                    isServerSideShippingCallbacksEnabled,
                );
            }

            if (isServerSideShippingCallbacksEnabled) {
                await this.paymentIntegrationService.loadCheckout();
            }

            await this.paymentIntegrationService.submitOrder({}, { params: { methodId } });

            await this.bigCommercePaymentsIntegrationService.submitPayment(methodId, data.orderID);

            if (onComplete && typeof onComplete === 'function') {
                onComplete();
            }

            return true; // FIXME: Do we really need to return true here?
        } catch (error) {
            if (typeof error === 'string') {
                throw new Error(error);
            }

            throw error;
        }
    }

    private async onShippingAddressChange(
        data: ShippingAddressChangeCallbackPayload,
    ): Promise<void> {
        const address = this.bigCommercePaymentsIntegrationService.getAddress({
            city: data.shippingAddress.city,
            countryCode: data.shippingAddress.countryCode,
            postalCode: data.shippingAddress.postalCode,
            stateOrProvinceCode: data.shippingAddress.state,
        });

        try {
            // Info: we use the same address to fill billing and shipping addresses to have valid quota on BE for order updating process
            // on this stage we don't have access to valid customer's address accept shipping data
            await this.paymentIntegrationService.updateBillingAddress(address);
            await this.paymentIntegrationService.updateShippingAddress(address);

            const shippingOption =
                this.bigCommercePaymentsIntegrationService.getShippingOptionOrThrow();

            await this.paymentIntegrationService.selectShippingOption(shippingOption.id);
            await this.bigCommercePaymentsIntegrationService.updateOrder();
        } catch (error) {
            if (typeof error === 'string') {
                throw new Error(error);
            }

            throw error;
        }
    }

    private async onShippingOptionsChange(
        data: ShippingOptionChangeCallbackPayload,
    ): Promise<void> {
        const shippingOption = this.bigCommercePaymentsIntegrationService.getShippingOptionOrThrow(
            data.selectedShippingOption.id,
        );

        try {
            await this.paymentIntegrationService.selectShippingOption(shippingOption.id);
            await this.bigCommercePaymentsIntegrationService.updateOrder();
        } catch (error) {
            if (typeof error === 'string') {
                throw new Error(error);
            }

            throw error;
        }
    }
}
